import { useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import AppendableBlocks from "@/components/feature/AppendableBlocks";
import { defaultSpacesSection, type SpacesSectionData, type SpaceOption } from "@/data/siteContent";

interface SpacesSectionProps { dataKey?: string; }

export default function SpacesSection({ dataKey }: SpacesSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [data, setData] = useAdminStorage<SpacesSectionData>(dataKey ?? "ma_spaces_v2", defaultSpacesSection);

  const handleApply = () => {
    const el = document.querySelector("#application");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const set = useCallback(<K extends keyof SpacesSectionData>(key: K, val: SpacesSectionData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateSpace = useCallback((id: string, patch: Partial<SpaceOption>) => {
    setData({ ...data, spaces: data.spaces.map((s) => s.id === id ? { ...s, ...patch } : s) });
  }, [data, setData]);

  const updateDetail = useCallback((id: string, idx: number, val: string) => {
    const space = data.spaces.find((s) => s.id === id);
    if (!space) return;
    const details = space.details.map((d, i) => i === idx ? val : d);
    updateSpace(id, { details });
  }, [data, updateSpace]);

  const addDetail = useCallback((id: string) => {
    const space = data.spaces.find((s) => s.id === id);
    if (!space) return;
    updateSpace(id, { details: [...space.details, "New detail"] });
  }, [data, updateSpace]);

  const removeDetail = useCallback((id: string, idx: number) => {
    const space = data.spaces.find((s) => s.id === id);
    if (!space) return;
    updateSpace(id, { details: space.details.filter((_, i) => i !== idx) });
  }, [data, updateSpace]);

  const addSpace = useCallback(() => {
    const newSpace: SpaceOption = {
      id: `space-${Date.now()}`,
      title: "New Space Type",
      subtitle: "For makers looking for a new space.",
      price: "$0/mo",
      term: "3 month minimum.",
      details: [
        "Shelf provided.",
        "Display provided by artist.",
        "Dimensions: 00 in x 00 in x 00 in",
        "4 tiers",
      ],
      highlight: false,
      image: "",
    };
    setData({ ...data, spaces: [...data.spaces, newSpace] });
  }, [data, setData]);

  const removeSpace = useCallback((id: string) => {
    setData({ ...data, spaces: data.spaces.filter((s) => s.id !== id) });
  }, [data, setData]);

  const bg = isDark ? "bg-gray-900" : undefined;
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";

  return (
    <section id="spaces" className={`py-24 px-6 transition-colors duration-300 ${bg ?? ""}`} style={isDark ? {} : { background: "#fff9fb" }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          {(isAdmin || data.eyebrow) && (
            isAdmin ? (
              <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} onDelete={() => set("eyebrow", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
            )
          )}
          {(isAdmin || data.heading) && (
            isAdmin ? (
              <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-3 mb-4" inputClassName="text-4xl font-extrabold text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
            )
          )}
          {(isAdmin || data.subheading) && (
            isAdmin ? (
              <AdminEditField value={data.subheading} onSave={(v) => set("subheading", v)} onDelete={() => set("subheading", "")} multiline inputClassName="text-lg text-center">
                <p className={`text-lg max-w-xl mx-auto ${subColor}`}>{data.subheading}</p>
              </AdminEditField>
            ) : (
              <p className={`text-lg max-w-xl mx-auto ${subColor}`}>{data.subheading}</p>
            )
          )}
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.spaces.map((space) => (
            <SpaceCard
              key={space.id}
              space={space}
              isAdmin={isAdmin}
              onApply={handleApply}
              onUpdate={(patch) => updateSpace(space.id, patch)}
              onUpdateDetail={(idx, val) => updateDetail(space.id, idx, val)}
              onAddDetail={() => addDetail(space.id)}
              onRemoveDetail={(idx) => removeDetail(space.id, idx)}
              onRemove={() => removeSpace(space.id)}
            />
          ))}
        </div>

        {/* Add space button (admin) */}
        {isAdmin && (
          <button
            onClick={addSpace}
            className="mt-6 w-full border-2 border-dashed border-pink-300 rounded-2xl py-5 text-pink-400 hover:text-pink-600 hover:border-pink-400 hover:bg-pink-50/50 transition-all cursor-pointer flex items-center justify-center gap-2 font-semibold text-sm"
          >
            <i className="ri-add-circle-line text-xl"></i>
            Add New Space Type
          </button>
        )}

        {isAdmin ? (
          <AdminEditField value={data.bottomHelperText} onSave={(v) => set("bottomHelperText", v)} className="mt-10 text-center" inputClassName="text-sm text-center">
            <p className={`text-center text-sm mt-10 ${subColor}`}>
              {data.bottomHelperText.includes("Contact us") ? (
                <>
                  {data.bottomHelperText.split("Contact us")[0]}
                  <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }} className="text-pink-500 font-semibold hover:underline cursor-pointer">Contact us</a>
                  {data.bottomHelperText.split("Contact us")[1]}
                </>
              ) : data.bottomHelperText}
            </p>
          </AdminEditField>
        ) : (
          <p className={`text-center text-sm mt-10 ${subColor}`}>
            {data.bottomHelperText.includes("Contact us") ? (
              <>
                {data.bottomHelperText.split("Contact us")[0]}
                <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }} className="text-pink-500 font-semibold hover:underline cursor-pointer">Contact us</a>
                {data.bottomHelperText.split("Contact us")[1]}
              </>
            ) : data.bottomHelperText}
          </p>
        )}

        <AppendableBlocks blocks={data.extraBlocks ?? []} onChange={(v) => set("extraBlocks", v)} className="mt-10" />
      </div>
    </section>
  );
}

interface SpaceCardProps {
  space: SpaceOption;
  isAdmin: boolean;
  onApply: () => void;
  onUpdate: (patch: Partial<SpaceOption>) => void;
  onUpdateDetail: (idx: number, val: string) => void;
  onAddDetail: () => void;
  onRemoveDetail: (idx: number) => void;
  onRemove: () => void;
}

function SpaceCard({ space, isAdmin, onApply, onUpdate, onUpdateDetail, onAddDetail, onRemoveDetail, onRemove }: SpaceCardProps) {
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const titleColor = isDark ? "text-white" : "text-gray-900";
  const detailColor = isDark ? "text-gray-400" : "text-gray-600";
  const termColor = isDark ? "text-gray-200" : "text-gray-700";

  return (
    <div className={`relative rounded-2xl flex flex-col transition-transform hover:-translate-y-1 border overflow-hidden ${cardBg}`}>
      {/* Image */}
      <div className="w-full h-52 overflow-hidden relative">
        {isAdmin ? (
          <AdminImageUpload
            src={space.image}
            alt={space.title}
            onReplace={(url) => onUpdate({ image: url })}
            objectPosition={space.imagePosition ?? "50% 50%"}
            onPositionChange={(pos) => onUpdate({ imagePosition: pos })}
            mobileObjectPosition={(space as typeof space & { mobileImagePosition?: string }).mobileImagePosition ?? space.imagePosition ?? "50% 50%"}
            onMobilePositionChange={(pos) => onUpdate({ mobileImagePosition: pos } as Partial<SpaceOption>)}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <img
            src={space.image}
            alt={space.title}
            className="w-full h-full object-cover"
            style={{ objectPosition: space.imagePosition ?? "50% 50%" }}
          />
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Admin delete button — inside content area, not over image */}
        {isAdmin && (
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              {isAdmin ? (
                <>
                  <AdminEditField value={space.title} onSave={(v) => onUpdate({ title: v })} className="mb-1" inputClassName="font-extrabold text-xl">
                    <h3 className={`text-xl font-extrabold ${titleColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{space.title}</h3>
                  </AdminEditField>
                  <AdminEditField value={space.subtitle} onSave={(v) => onUpdate({ subtitle: v })} className="mb-3" inputClassName="text-xs uppercase tracking-wide text-pink-500 font-semibold">
                    <p className="text-xs text-pink-500 font-semibold uppercase tracking-wide">{space.subtitle}</p>
                  </AdminEditField>
                  <AdminEditField value={space.price} onSave={(v) => onUpdate({ price: v })} className="mb-3" inputClassName="text-2xl font-extrabold text-green-600">
                    <div className="text-2xl font-extrabold text-green-600">{space.price}</div>
                  </AdminEditField>
                </>
              ) : null}
            </div>
            <button
              onClick={onRemove}
              className="ml-2 flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors"
              title="Remove this space type"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </div>
        )}

        {!isAdmin && (
          <>
            <h3 className={`text-xl font-extrabold mb-1 ${titleColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{space.title}</h3>
            <p className="text-xs text-pink-500 font-semibold uppercase tracking-wide mb-3">{space.subtitle}</p>
            <div className="text-2xl font-extrabold text-green-600 mb-3">{space.price}</div>
          </>
        )}

        <ul className={`text-sm space-y-1 mb-4 flex-1 ${detailColor}`}>
          {isAdmin ? (
            <li className="mb-1">
              <AdminEditField value={space.term} onSave={(v) => onUpdate({ term: v })} inputClassName="text-sm font-semibold text-gray-700">
                <span className={`font-semibold ${termColor}`}>Term: {space.term}</span>
              </AdminEditField>
            </li>
          ) : (
            <li className={`font-semibold ${termColor}`}>Term: {space.term}</li>
          )}
          {space.details.map((d, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <i className="ri-checkbox-circle-line text-green-400 mt-0.5 flex-shrink-0"></i>
              {isAdmin ? (
                <div className="flex items-center gap-1 flex-1">
                  <AdminEditField value={d} onSave={(v) => onUpdateDetail(i, v)} className="flex-1" inputClassName="text-sm">
                    <span>{d}</span>
                  </AdminEditField>
                  <button onClick={() => onRemoveDetail(i)} className="w-5 h-5 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer flex-shrink-0" title="Remove">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              ) : (
                <span>{d}</span>
              )}
            </li>
          ))}
          {isAdmin && (
            <li>
              <button onClick={onAddDetail} className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1">
                <i className="ri-add-line"></i> Add detail
              </button>
            </li>
          )}
        </ul>

        <button onClick={onApply} className="w-full py-2.5 rounded-full text-sm font-bold transition-colors whitespace-nowrap cursor-pointer bg-pink-100 hover:bg-pink-200 text-pink-700">
          Apply Now
        </button>
      </div>
    </div>
  );
}
